import type {
  OrgChartNode,
  OrgChartPending,
  OrgChartQuery,
  OrgChartResponse,
} from "@evolua/api-client";

const BRUNO = "22222222-2222-4222-8222-222222222222";
const ANA = "11111111-1111-4111-8111-111111111111";
const CARLA = "33333333-3333-4333-8333-333333333333";
const ELENA = "44444444-4444-4444-8444-444444444444";

type Person = {
  id: string;
  fullName: string;
  status: string;
  positionName: string;
  departmentName: string;
  managerId: string | null;
};

const PEOPLE: Person[] = [
  {
    id: BRUNO,
    fullName: "Bruno Costa",
    status: "ACTIVE",
    positionName: "Diretor",
    departmentName: "Operações",
    managerId: null,
  },
  {
    id: ANA,
    fullName: "Ana Silva",
    status: "ACTIVE",
    positionName: "Analista",
    departmentName: "RH",
    managerId: BRUNO,
  },
  {
    id: CARLA,
    fullName: "Carla Dias",
    status: "ON_LEAVE",
    positionName: "Analista",
    departmentName: "Operações",
    managerId: BRUNO,
  },
  {
    id: ELENA,
    fullName: "Elena Rocha",
    status: "ACTIVE",
    positionName: "Analista",
    departmentName: "Financeiro",
    managerId: "99999999-9999-4999-8999-999999999999",
  },
];

function childrenOf(parentId: string): Person[] {
  return PEOPLE.filter((p) => p.managerId === parentId).sort((a, b) =>
    a.fullName.localeCompare(b.fullName, "pt-BR"),
  );
}

function isOrphan(p: Person): boolean {
  return !p.managerId || !PEOPLE.some((x) => x.id === p.managerId);
}

function toNode(p: Person, withKids = false, depthLeft = 0): OrgChartNode {
  const kids = childrenOf(p.id);
  return {
    id: p.id,
    fullName: p.fullName,
    status: p.status,
    positionName: p.positionName,
    departmentName: p.departmentName,
    managerId: p.managerId,
    childrenCount: kids.length,
    orphan: isOrphan(p),
    children:
      withKids && depthLeft > 0 ? kids.map((c) => toNode(c, true, depthLeft - 1)) : [],
  };
}

export function getDemoOrgChart(query: OrgChartQuery = {}): OrgChartResponse {
  const pending = PEOPLE.filter(isOrphan);
  const asOf = new Date().toISOString();
  if (query.q?.trim()) {
    const q = query.q.toLowerCase();
    return {
      data: PEOPLE.filter((p) => p.fullName.toLowerCase().includes(q)).map((p) =>
        toNode(p),
      ),
      totalEmployees: PEOPLE.length,
      pendingWithoutManager: pending.length,
      asOf,
      mode: "search",
    };
  }
  if (query.parentId) {
    return {
      data: childrenOf(query.parentId).map((p) => toNode(p)),
      totalEmployees: PEOPLE.length,
      pendingWithoutManager: pending.length,
      asOf,
      mode: "children",
    };
  }
  if (query.focusId) {
    const root = PEOPLE.find((p) => p.id === query.focusId);
    if (!root) throw new Error("Colaborador não encontrado");
    const maxDepth = Math.min(Math.max(query.maxDepth ?? 2, 0), 6);
    return {
      data: [toNode(root, true, maxDepth)],
      totalEmployees: PEOPLE.length,
      pendingWithoutManager: pending.length,
      asOf,
      mode: "focus",
    };
  }
  return {
    data: pending.map((p) => toNode(p)),
    totalEmployees: PEOPLE.length,
    pendingWithoutManager: pending.length,
    asOf,
    mode: "roots",
  };
}

export function getDemoOrgChartPending(): OrgChartPending {
  const data = PEOPLE.filter(isOrphan).map((p) => toNode(p));
  return { data, total: data.length };
}
